import {AttachmentFileMetadata} from "@/shared/timr";
import {Notify} from "@/shared/ui/notify";

import * as AttachmentUtil from "../attachments/attachmentUtil";
import * as FileUtil from "../attachments/fileUtil";
import {htmlEscape} from "../utils/string";
import {formatDate, parseInstant, Temporal} from "@/shared/utils/datefunctions";

/**
 * Interface for existing files that are already in S3 (or elsewhere).
 * We store both the URL and the MIME type (e.g., "image/jpeg", "application/pdf", etc.)
 */
interface ExistingFileData {
	url: string;
	attachmentId: string;
	metadata: AttachmentFileMetadata;
}

enum SortMode {
	/**
	 * Sort by last modified date, newest first (default).
	 */
	fileLastModified = "fileLastModified",

	/**
	 * Sort by file name alphabetically.
	 */
	name = "name"
}

export interface FileSelectorOptions {
	/**
	 * Callback function to be called when files are added via drag & drop or file picker.
	 * If this function returns false, the files will not be added to the upload list.
	 */
	filePredicate?: (file: File) => boolean;

	/**
	 * The accept string (e.g., "image/*,.pdf,application/json")
	 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/accept
	 */
	acceptString?: string;

	maxFiles?: number;

	maxFileSize?: number;
}

const template = `
	<div class="outer-container">
		<div class="add-container">
			<div class="add-message-icon">
				<i class="fa-solid fa-upload"></i>
			</div>
			<div>
				${messages["attachments.dragAndDrop"]}
			</div>
		</div>
		<div class="file-gallery">
			<div class="gallery-arrow arrow-left">
				<i class="fa-solid fa-chevron-left"></i>
			</div>
			<div class="gallery-arrow arrow-right">
				<i class="fa-solid fa-chevron-right"></i>
			</div>
			<div class="file-gallery-viewport">
			
			</div>
		</div>
		<div class="readonly-empty-container">
			<div class="readonly-empty-message"></div>
		</div>
	</div>
	`;

// Actual payloads are placed inside .gallery-thumb after .action-overlay
const galleryItemTemplate = `
				<div class="action-overlay">
					<span>
						<a href="#" data-action="delete"><i class="fa-solid fa-trash"></i></a>
						<a href="#" class="ml-5" data-action="download"><i class="fa-solid fa-download"></i></a>
					</span>
				</div>
				<i class="state-info fa-solid"></i>
	`;

/**
 * Less file for this component: ./limitless_timr/components/attachmentGallery.less
 */
export class AttachmentGallery extends HTMLElement {

	/**
	 * Event fires whenever files are added or removed
	 * Note that this is fire if one of these arrays changes:
	 * - filesToUpload
	 * - existingFiles
	 * - attachmentsToDelete
	 *
	 * The event does not have a detail payload, so listeners need to call the respective getters to get the current state.
	 */
	public static readonly EVENT_FILES_CHANGED = "attachments-changed";

	private static readonly READONLY_ATTRIBUTE = "readonly";
	private static readonly SORT_ATTRIBUTE = "sort";
	static readonly observedAttributes = [AttachmentGallery.READONLY_ATTRIBUTE, AttachmentGallery.SORT_ATTRIBUTE];

	private static preventDragDropDefaultsRegistered = false;

	private mainContainer!: HTMLDivElement;

	/**
	 * Hidden file input. Is just used to trigger the native file picker dialog.
	 */
	private hiddenFileInput!: HTMLInputElement;

	/**
	 * The actual scrolled content area, containing the files
	 */
	private galleryViewport!: HTMLDivElement;

	private galleryContainer!: HTMLDivElement;

	private readonlyEmptyContainer!: HTMLDivElement;
	private readonlyEmptyMessage!: HTMLDivElement;

	private emptyMessage: string | null = null;

	private filesToUpload: File[] = [];
	private existingFiles: ExistingFileData[] = [];
	private attachmentsToDelete: ExistingFileData[] = [];

	private isBuilt = false;
	private dragCounter = 0;

	private selectorOptions?: FileSelectorOptions = {};

	private onAttachmentClick?: (file: File | ExistingFileData, element: this) => void;

	/**
	 * MIME type -> Font Awesome icon map for non-images.
	 * "default" is used if none match.
	 */
	private readonly mimeIcons: { [key: string]: string } = {
		"application/pdf": "fa-file-pdf",
		"application/vnd.ms-excel": "fa-file-excel",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "fa-file-excel",
		"application/msword": "fa-file-word",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document": "fa-file-word",
		"application/zip": "fa-file-zipper",
		"video/mp4": "fa-file-video",
		"default": "fa-file"
	};

	constructor() {
		super();
	}

	connectedCallback() {
		if (this.isBuilt) return;

		if (!AttachmentGallery.preventDragDropDefaultsRegistered) {
			AttachmentGallery.preventDragDropDefaultsRegistered = true;
			this.registerPreventDragDropDefaults();
		}

		this.innerHTML = template;
		this.initElements();

		// Initial render
		this.renderGallery();

		this.isBuilt = true;
	}

	attributeChangedCallback(_name: string, _oldValue: string | null, _newValue: string | null) {
		// Currently, we only have one attribute: readonly.
		// As all visibility is handled via css there is no need to
		// rebuild the component or re-render the gallery.
	}

	// Define property access
	get readonly() {
		return this.hasAttribute(AttachmentGallery.READONLY_ATTRIBUTE);
	}

	set readonly(value) {
		if (value) {
			this.setAttribute(AttachmentGallery.READONLY_ATTRIBUTE, '');
		} else {
			this.removeAttribute(AttachmentGallery.READONLY_ATTRIBUTE);
		}
	}

	get sort() {
		const rawValue = this.getAttribute(AttachmentGallery.SORT_ATTRIBUTE);

		// Check if the value is a valid SortMode
		if (rawValue && Object.values(SortMode).includes(rawValue as SortMode)) {
			return rawValue as SortMode;
		}

		return SortMode.fileLastModified; // Default sort mode
	}

	set sort(value: SortMode | null) {
		if (value) {
			this.setAttribute(AttachmentGallery.SORT_ATTRIBUTE, value);
		} else {
			this.removeAttribute(AttachmentGallery.SORT_ATTRIBUTE);
		}
	}

	public setFileSelectorOptions(options?: FileSelectorOptions) {
		this.selectorOptions = options;
		this.hiddenFileInput.accept = options?.acceptString || "";
	}

	public setOnAttachmentClick(callback: (file: File | ExistingFileData, element: this) => void) {
		this.onAttachmentClick = callback;
	}

	public setEmptyMessage(message: string): void {
		this.emptyMessage = message;
		if (this.isBuilt) {
			this.renderGallery();
		}
	}

	private initElements() {
		// Outer container for drag highlight
		this.mainContainer = this.querySelector("div.outer-container");

		this.querySelector("a.open-file-dialog-link")
			.addEventListener("click", (e) => {
				e.preventDefault();
				this.hiddenFileInput.click();
			});

		this.galleryContainer = this.querySelector("div.file-gallery");
		this.galleryViewport = this.querySelector("div.file-gallery-viewport");
		this.readonlyEmptyContainer = this.querySelector("div.readonly-empty-container");
		this.readonlyEmptyMessage = this.querySelector("div.readonly-empty-message");

		this.querySelector("div.arrow-left").addEventListener("click", () => {
			this.galleryViewport.scrollBy({left: -100, behavior: "smooth"});
		});

		this.querySelector("div.arrow-right").addEventListener("click", () => {
			this.galleryViewport.scrollBy({left: 100, behavior: "smooth"});
		});

		// Hidden input for file selection
		this.hiddenFileInput = document.createElement("input");
		this.hiddenFileInput.type = "file";
		this.hiddenFileInput.multiple = true;
		// NOTE: It's not possible to allow file and directory selection at the same time
		// However, it is possible to drag and drop both
		// this.fileInput.webkitdirectory = true;
		this.hiddenFileInput.style.display = "none";
		this.hiddenFileInput.addEventListener("change", (e) => {
			const target = e.target as HTMLInputElement;
			if (!target.files) return;
			this.addFiles(Array.from(target.files));
			target.value = ""; // reset
			this.renderGallery();
		});

		// Drag & drop on the mainContainer
		this.mainContainer.addEventListener("dragenter", e => this.handleDragEnter(e));
		this.mainContainer.addEventListener("dragover", e => e.preventDefault());
		this.mainContainer.addEventListener("dragleave", e => this.handleDragLeave(e));
		this.mainContainer.addEventListener("drop", e => void this.handleDrop(e));

		if (typeof ResizeObserver != "undefined") {
			// Browser support is good but since it's not a critical functionality we check first
			// Refresh arrow visibility on resize
			const resizeObserver = new ResizeObserver((_entries) => {
				this.refreshNavigationArrows();
			});

			resizeObserver.observe(this.galleryViewport);
		}
	}

	/**
	 * If the page contains a file gallery, we globally disable the browser default drag & drop behavior.
	 * This is to prevent opening a new tab when accidentally dropping the file outside the intended area.
	 */
	private registerPreventDragDropDefaults() {
		const preventDefaults = (e: Event) => {
			e.preventDefault();
			e.stopPropagation();
		}

		['dragover', 'drop'].forEach(eventName => {
			document.addEventListener(eventName, preventDefaults, false);
		});
	}

	/**
	 * Completely clear everything: new files, existing files, and removed files.
	 * This sets the component back to a fully empty state.
	 */
	public reset() {
		this.filesToUpload = [];
		this.attachmentsToDelete = [];
		this.existingFiles = [];
		this.dispatchEvent(new CustomEvent(AttachmentGallery.EVENT_FILES_CHANGED));
		this.renderGallery();
	}

	// Drag events to highlight the container
	private handleDragEnter(e: DragEvent) {
		if (this.readonly) {
			return;
		}

		e.preventDefault();
		this.dragCounter++;
		this.mainContainer.classList.add("drag-over");
	}

	private handleDragLeave(e: DragEvent) {
		if (this.readonly) {
			return;
		}

		e.preventDefault();
		this.dragCounter--;
		if (this.dragCounter === 0) {
			this.mainContainer.classList.remove("drag-over");
		}
	}

	private async handleDrop(e: DragEvent) {
		if (this.readonly) {
			return;
		}

		e.preventDefault();
		this.dragCounter = 0;
		this.mainContainer.classList.remove("drag-over");

		if (!e.dataTransfer) return;
		const droppedFiles = await FileUtil.readAllFiles(e.dataTransfer.items);
		this.addFiles(droppedFiles);
		this.renderGallery();
	}

	public setExistingFiles(files: ExistingFileData[]) {
		this.existingFiles = files.slice(); // create a shallow copy
		this.sortList(this.existingFiles);
		this.dispatchEvent(new CustomEvent(AttachmentGallery.EVENT_FILES_CHANGED, {bubbles: true}));
		this.renderGallery();
	}

	public getSerializedArrayElements(): JQuerySerializeArrayElement[] {
		const array: JQuerySerializeArrayElement[] = [];

		this.getFilesToUpload()
			.map((file, i) => [
				{name: "uploadFilesMetadata[" + i + "].fileName", value: file.name},
				{name: "uploadFilesMetadata[" + i + "].size", value: file.size.toString()},
				{name: "uploadFilesMetadata[" + i + "].contentType", value: file.type},
				{name: "uploadFilesMetadata[" + i + "].fileLastModified", value: file.lastModified.toString()}
			])
			.forEach(entries => array.push(...entries));

		this.getAttachmentsToDelete()
			.map((file, i) => [
				{name: "attachmentsToDelete[" + i + "]", value: file.attachmentId}
			])
			.forEach(entries => array.push(...entries));

		return array;
	}

	public getFilesToUpload(): File[] {
		return this.filesToUpload;
	}

	public getAttachmentsToDelete(): ExistingFileData[] {
		return this.attachmentsToDelete;
	}

	private addFiles(files: File[]) {
		const valid: File[] = []
		const invalid: File[] = []

		files.forEach((file) => {
			const isDuplicate = this.filesToUpload.some(
				(f) => f.name === file.name && f.size === file.size
			);

			if (isDuplicate) {
				return;
			}

			if (this.selectorOptions?.acceptString != null && !FileUtil.isFileAccepted(file, this.selectorOptions.acceptString)) {
				invalid.push(file);
				return;
			}

			if (this.selectorOptions?.maxFileSize != null && file.size > this.selectorOptions.maxFileSize) {
				invalid.push(file);
				return;
			}

			if (this.selectorOptions?.filePredicate != null && !this.selectorOptions.filePredicate(file)) {
				invalid.push(file)
				return;
			}

			valid.push(file);
		});

		if (invalid.length > 0) {
			console.debug("Some files are invalid", invalid);
			const fileNames = invalid.map(f => f.name).join(", ");
			Notify.danger(messages.withArguments("attachments.validate.invalid", fileNames));
		}

		let totalFiles = this.filesToUpload.length + this.existingFiles.length;

		this.sortList(valid);
		// The elements are added to the beginning of the list using unshift, so we have to reverse the list
		// one time to keep the sorted order
		valid.reverse()

		let addedFiles = false;
		for (const file of valid) {
			if (this.selectorOptions?.maxFiles && totalFiles >= this.selectorOptions.maxFiles) {
				console.debug("Maximum number of files reached. Cannot add more.");
				Notify.danger(messages.withArguments("attachments.validate.tooMany", this.selectorOptions.maxFiles));
				break;
			}
			// Add to beginning of the list for better UX
			this.filesToUpload.unshift(file);
			addedFiles = true;
			totalFiles++;
		}
		if (addedFiles) {
			this.dispatchEvent(new CustomEvent(AttachmentGallery.EVENT_FILES_CHANGED));
		}
	}

	/**
	 * If triggered from an external source, it should only be called if this.isBuilt is true.
	 * @private
	 */
	private renderGallery() {
		// Clear the .file-gallery-viewport content
		while (this.galleryViewport.firstChild) {
			this.galleryViewport.removeChild(this.galleryViewport.firstChild);
		}

		// If no files, show placeholder text
		const totalFiles = this.existingFiles.length + this.filesToUpload.length;
		this.mainContainer.classList.toggle("has-files", totalFiles > 0);

		const showReadonlyEmpty = this.readonly && totalFiles === 0;
		if (showReadonlyEmpty && this.emptyMessage) {
			this.readonlyEmptyContainer.style.display = "block";
			this.readonlyEmptyMessage.innerHTML = this.emptyMessage;
			this.galleryContainer.style.display = "none";
		} else {
			this.readonlyEmptyContainer.style.display = "none";
			this.galleryContainer.style.display = "";
		}

		// Render newly added local files
		for (const file of this.filesToUpload) {
			const item = this.createThumbnail(file);
			this.galleryViewport.appendChild(item);
		}

		// Render existing S3 files
		for (const existing of this.existingFiles) {
			const item = this.createThumbnail(existing);
			this.galleryViewport.appendChild(item);
		}

		this.refreshNavigationArrows();
	}

	private sortList(attachments: File[] | ExistingFileData[]) {
		if (this.sort === SortMode.name) {
			attachments.sort((a: File | ExistingFileData, b: File | ExistingFileData) => {
				const nameA = a instanceof File ? a.name : a.metadata.fileName;
				const nameB = b instanceof File ? b.name : b.metadata.fileName;
				return nameA.localeCompare(nameB); // Sort by name
			});
		}
	}

	private refreshNavigationArrows() {
		if (!this.isBuilt) return;

		const hasOverflow = (this.galleryViewport.scrollWidth > this.galleryViewport.clientWidth);
		this.galleryContainer.classList.toggle("has-overflow", hasOverflow);
	}

	private createThumbnail(item: File | ExistingFileData): HTMLDivElement {
		const galleryItem: HTMLDivElement = document.createElement("div");
		galleryItem.className = "gallery-item gallery-thumb center-content";
		galleryItem.innerHTML = galleryItemTemplate;

		if (item instanceof File) {
			galleryItem.setAttribute("title", item.name);
		} else {
			galleryItem.setAttribute("title", item.metadata.fileName);
		}

		const thumbContent = this.createThumbnailContent(item);
		galleryItem.appendChild(thumbContent);

		let fullScreenLink: HTMLAnchorElement | null = null;

		if (thumbContent instanceof HTMLImageElement) {
			fullScreenLink = this.createLightboxLink(item)
			galleryItem.appendChild(fullScreenLink);
		}

		if (this.onAttachmentClick || fullScreenLink) {
			galleryItem.classList.add("cursor-pointer");
			galleryItem.addEventListener("click", () => {
				if (this.onAttachmentClick) {
					this.onAttachmentClick(item, this);
				}
				if (fullScreenLink) {
					fullScreenLink.click();
				}
			});
		}

		galleryItem.querySelector("a[data-action='delete']")
			.addEventListener("click", (evt) => {
				evt.preventDefault();
				evt.stopPropagation(); // to not trigger onAttachmentClick above
				this.deleteFile(item);
			});

		galleryItem.querySelector("a[data-action='download']")
			.addEventListener("click", (evt) => {
				evt.preventDefault();
				evt.stopPropagation(); // to not trigger onAttachmentClick above
				if (item instanceof File) {
					// Download local file
					const blobUrl = URL.createObjectURL(item);
					this.downloadFile(blobUrl, item.name);
				} else {
					// existing file
					this.downloadFile(item.url, item.url.split("/").pop() || "file");
				}
			});

		if (item instanceof File) {
			// Add "needs upload" indicator for local files
			const stateInfo = galleryItem.querySelector("i.state-info");
			stateInfo.classList.add("fa-cloud-upload");
			stateInfo.classList.add("text-primary");
		}

		return galleryItem;
	}

	private deleteFile(item: ExistingFileData | File) {
		if (item instanceof File) {
			this.filesToUpload = this.filesToUpload.filter((f) => f !== item);
		} else {
			this.existingFiles = this.existingFiles.filter((x) => x.url !== item.url);
			this.attachmentsToDelete.push(item);
		}
		this.dispatchEvent(new CustomEvent(AttachmentGallery.EVENT_FILES_CHANGED));
		this.renderGallery();
	}

	private createThumbnailContent(item: File | ExistingFileData): HTMLDivElement | HTMLImageElement {
		// Determine if image
		let mimeType: string;
		let fileName: string;
		let size: number;
		let lastModified: Temporal.Instant | null;

		if (item instanceof File) {
			mimeType = item.type;
			fileName = item.name;
			size = item.size;
			lastModified = Temporal.Instant.fromEpochMilliseconds(item.lastModified);
		} else {
			mimeType = item.metadata.contentType;
			fileName = item.metadata.fileName;
			size = item.metadata.size;
			lastModified = parseInstant(item.metadata.fileLastModified);
		}
		const isImage = mimeType.startsWith("image/");

		if (isImage) {
			// We display an <img>
			const img = document.createElement("img");
			img.alt = fileName;

			if (item instanceof File) {
				const reader = new FileReader();
				reader.onload = (e) => {
					if (e.target?.result) {
						img.src = e.target.result as string;
					}
				};
				reader.readAsDataURL(item);
			} else {
				// existing file
				img.src = item.url;
			}

			return img;
		} else {
			// Non-image placeholder
			const placeholder = document.createElement("div");
			placeholder.className = "non-image-placeholder";

			const iconContainer = document.createElement("div");
			iconContainer.className = "icon-container center-content";

			const iconClass = this.mimeIcons[mimeType] || this.mimeIcons["default"];
			iconContainer.innerHTML = `<i class="fa-solid ${iconClass}"></i>`;
			placeholder.appendChild(iconContainer);

			const infoBox = document.createElement("div");
			infoBox.className = "info-box";

			infoBox.innerHTML = `
								<div class="main-text">${htmlEscape(fileName)}</div>
								<div class="small-text">
									<span>${AttachmentUtil.formatFileSize(size)}</span>
									<span class="pull-right">${formatDate(lastModified?.toZonedDateTimeISO(Temporal.Now.timeZoneId()))}</span>
								</div>
								`;
			placeholder.appendChild(infoBox);

			return placeholder;
		}
	}

	private createLightboxLink(file: File | ExistingFileData): HTMLAnchorElement {
		const a = document.createElement('a');

		if (file instanceof File) {
			const reader = new FileReader();
			reader.onload = (e) => {
				if (e.target?.result) {
					a.href = e.target.result as string;
				}
			};
			reader.readAsDataURL(file);
		} else {
			// existing file
			a.href = file.url
		}
		a.dataset.lightbox = 'attachment-gallery#' + this.id;
		a.dataset.title = file instanceof File ? file.name : file.metadata.fileName;

		return a;
	}

	private downloadFile(url: string, name: string) {
		const a = document.createElement("a");
		a.href = url;
		a.download = name;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}
}

// @ts-expect-error lightbox is a global variable provided by the lightbox library
// eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
lightbox.option({
	fadeDuration: 200,
	imageFadeDuration: 200,
	alwaysShowNavOnTouchDevices: true
})
